import {
  Box,
  Col,
  FormItem,
  Icon,
  RadioButton,
  RadioGroup,
  Row,
  Text,
  TreeSelection,
} from '@cubeyou-team/hx-react-components';
import { TaxonomySelector } from '@discover-frontend/components';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { DATA_SOURCE_IDS } from '@discover-frontend/constants';
import { getPopulationLabel } from '@discover-frontend/utils';
import translations from '../../../../lang';

const StyledQueryAdvancedOptions = styled.div`
  height: 100%;
  background-color: ${(props) => props.theme.colors.light_dark};
`;

const StyledOptions = styled.div`
  height: 100%;
  overflow: auto !important;
`;

const getCurrentBaseOption = (query) => {
  if (!query.base_query || query.base_query.whole_population) {
    return { id: 'all', label: getPopulationLabel() };
  }

  return {
    ...query.base_query.query_block.arguments_list[0],
  };
};

const QueryAdvancedOptions = ({
  query,
  timeframes,
  taxonomyList,
  onAudienceTimeframeChange,
  onChangeBaseQueryRequest,
  onResultTimeframeChange,
  onQueryTypeChange,
}) => {
  const dataSource = useSelector((state) => state.queryBuilder.dataSource);
  const enableTimedInterest = dataSource === DATA_SOURCE_IDS.TWITTER;

  if (timeframes.isLoading) return null;

  const { query_type } = query;
  const currentBase = getCurrentBaseOption(query);

  const handleOnRadioChange = useCallback(onQueryTypeChange, [
    onQueryTypeChange,
  ]);

  const radioOptions = useMemo(() => {
    return [
      {
        id: 'interest',
        label: 'Interest',
        icon: 'heart',
        description: translations['en-US'].QUERY_BUILDER.interest,
        enabled: true,
      },
      {
        id: 'engagement',
        label: 'Timed Interest',
        icon: 'calendar',
        description: translations['en-US'].QUERY_BUILDER.timedInterest,
        enabled: enableTimedInterest,
      },
    ].filter((option) => option.enabled);
  }, [enableTimedInterest]);

  if (radioOptions.length === 0) return null;

  return (
    <StyledQueryAdvancedOptions>
      <Box pX="large" pY="medium" style={{ height: '100%' }}>
        <StyledOptions id="gtAdvancedOptions">
          <Box pY="medium">
            <RadioGroup
              name="interest"
              onChange={({ target: { value } }) => handleOnRadioChange(value)}
              selected={query_type}
              size="standard"
            >
              {radioOptions.map((option) => (
                <Row
                  halign="left"
                  valign="middle"
                  gutter="small"
                  style={{ width: '100%' }}
                  key={option.id}
                >
                  <Col span="auto" style={{ width: '100%' }}>
                    <Box pR="xlarge">
                      <RadioButton value={option.id}>
                        <Row halign="left" valign="middle">
                          <Col span="auto">
                            <Icon icon={option.icon} size="medium" />
                          </Col>
                          <Col span="auto">
                            <Text size="xbody" variant="dark" weight="bold">
                              {option.label}
                            </Text>
                          </Col>
                        </Row>
                      </RadioButton>
                      <Box pL="large" pB="large" style={{ width: '100%' }}>
                        <Text size="xbody" variant="dark" weight="semi-bold">
                          {option.description}
                        </Text>
                      </Box>
                    </Box>
                  </Col>
                </Row>
              ))}
            </RadioGroup>
          </Box>
          <Row style={{ width: '100%' }}>
            {query_type === 'engagement' && (
              <Col span={12}>
                <FormItem
                  label="Audience timeframe"
                  buttonIconTooltipMessage={
                    translations['en-US'].QUERY_BUILDER.audienceTimeframeInfos
                  }
                  buttonIconTooltipPosition="left"
                  icon="informations"
                >
                  <TreeSelection
                    options={timeframes.data}
                    parentNotSelectable
                    onItemSelected={onAudienceTimeframeChange}
                    selectedOptionId={query.audience_timeframe}
                  />
                </FormItem>
              </Col>
            )}
            <Col span={12}>
              <FormItem
                label="Base"
                buttonIconTooltipMessage={
                  translations['en-US'].QUERY_BUILDER.baseInfos
                }
                buttonIconTooltipPosition="left"
                icon="informations"
                id="gtBase"
              >
                {!taxonomyList.isLoading && (
                  <TaxonomySelector
                    isQueryBuilder
                    allLabel={getPopulationLabel()}
                    taxonomy={taxonomyList.baseTaxonomyWithLifestyles}
                    selectedOptionId={currentBase.id}
                    parentNotSelectable
                    selectedItemType="item"
                    onTaxonomySelected={onChangeBaseQueryRequest}
                  />
                )}
              </FormItem>
            </Col>
            {query_type === 'engagement' && (
              <Col span={12}>
                <FormItem
                  label="Results timeframe"
                  buttonIconTooltipMessage={
                    translations['en-US'].QUERY_BUILDER.resultTimeframeInfos
                  }
                  buttonIconTooltipPosition="left"
                  icon="informations"
                >
                  <TreeSelection
                    options={timeframes.data}
                    parentNotSelectable
                    selectedOptionId={query.results_timeframe}
                    onItemSelected={onResultTimeframeChange}
                  />
                </FormItem>
              </Col>
            )}
          </Row>
        </StyledOptions>
      </Box>
    </StyledQueryAdvancedOptions>
  );
};

export default QueryAdvancedOptions;
